export const hslToRgb = (h: number, s: number, l: number) => {
  let r: number;
  let g: number;
  let b: number;

  if (s == 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = function hue2rgb(p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
};

export const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h: number;
  let s: number;
  const l = (max + min) / 2;

  if (max == min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h, s, l };
};

export const parseColor = (color: string): { r: number; g: number; b: number; a: number } => {
  if (!color) return;
  // check if color is in hex format
  if (color[0] === '#') {
    color = color.slice(1);
    const [r, g, b, a] = [color.slice(0, 2), color.slice(2, 4), color.slice(4, 6), color.slice(6, 8)].map(hex =>
      parseInt(hex, 16)
    );
    return { r, g, b, a: (a || 255) / 255 };
  }

  // check if color string is in rgb format
  if (color.startsWith('rgb(')) {
    const [r, g, b] = color
      .slice(4, -1)
      .split(',')
      .map(str => parseInt(str));
    return { r, g, b, a: 1 };
  }

  // check if color string is in rgba format
  if (color.startsWith('rgba(')) {
    const [r, g, b, a] = color
      .slice(5, -1)
      .split(',')
      .map(str => parseFloat(str));
    return { r, g, b, a: a || 1 };
  }

  // check if color string is in hsl format
  if (color.startsWith('hsl(')) {
    const [h, s, l] = color
      .slice(4, -1)
      .split(',')
      .map(str => parseFloat(str));
    const rgb = hslToRgb(h, s, l);
    return { ...rgb, a: 1 };
  }

  if (color === 'black') return { r: 0, g: 0, b: 0, a: 1 };
  if (color === 'white') return { r: 255, g: 255, b: 255, a: 1 };

  console.warn('color string is not in hex or rgb format');
};
